/**
 * AiVoice.js — MediaRecorder wrapper for voice input.
 * Records audio and returns a Blob for the /transcribe endpoint.
 */

export class AiVoice {
  constructor() {
    this._recorder = null;
    this._chunks   = [];
    this._stream   = null;
  }

  /** True if the browser supports voice recording. */
  isSupported() {
    return !!(navigator.mediaDevices?.getUserMedia && window.MediaRecorder);
  }

  /** True while actively recording. */
  isRecording() {
    return !!(this._recorder && this._recorder.state === 'recording');
  }

  /**
   * Start recording from the microphone.
   * @param {function(Blob): void} onDone  Called with the audio Blob when stopped.
   */
  async startRecording(onDone) {
    if (this._recorder) return;

    this._chunks = [];
    this._stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    const mimeType = MediaRecorder.isTypeSupported('audio/webm')
      ? 'audio/webm'
      : 'audio/mp4';

    this._recorder = new MediaRecorder(this._stream, { mimeType });

    this._recorder.ondataavailable = (e) => {
      if (e.data.size > 0) this._chunks.push(e.data);
    };

    this._recorder.onstop = () => {
      const blob = new Blob(this._chunks, { type: this._recorder.mimeType });
      this._stream.getTracks().forEach(t => t.stop());
      this._recorder = null;
      this._stream   = null;
      onDone(blob);
    };

    this._recorder.start();
  }

  /** Stop recording and trigger onDone callback. */
  stopRecording() {
    if (this._recorder && this._recorder.state !== 'inactive') {
      this._recorder.stop();
    }
  }
}
